/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - TimeZoneSelector
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file TimeZoneSelector.tsx
 * @description Selector de zona horaria con búsqueda y preview
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useMemo } from 'react';
import { Form, ListGroup, Badge, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faGlobe,
  faMapMarkerAlt,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { TimeZoneInfo } from '../../../types/scheduler.types';
import { useTimeZone } from '../../../hooks/useTimeZone';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface TimeZoneSelectorProps {
  value: string;
  onChange: (timezone: string) => void;
  showPreview?: boolean;
  previewDate?: Date;
  label?: string;
  className?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const TimeZoneSelector: React.FC<TimeZoneSelectorProps> = ({
  value,
  onChange,
  showPreview = true,
  previewDate,
  label = 'Zona Horaria',
  className = ''
}) => {
  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════════════
  
  const {
    timezones,
    currentTimezone,
    searchResults,
    search,
    clearSearch,
    searchQuery,
    getTimeInTz
  } = useTimeZone();

  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [isOpen, setIsOpen] = useState(false);
  const [localQuery, setLocalQuery] = useState('');

  // ═══════════════════════════════════════════════════════════════════════
  // COMPUTED
  // ═══════════════════════════════════════════════════════════════════════
  
  const selectedTimezone = useMemo(() => {
    return timezones.find(tz => tz.value === value);
  }, [timezones, value]);

  const detectedTimezone = useMemo(() => {
    return timezones.find(tz => tz.value === currentTimezone);
  }, [timezones, currentTimezone]);

  const displayTimezones = searchQuery ? searchResults : timezones;

  const timePreview = useMemo(() => {
    if (!showPreview || !previewDate) return null;
    return getTimeInTz(previewDate, value);
  }, [showPreview, previewDate, value, getTimeInTz]);

  // ═══════════════════════════════════════════════════════════════════════
  // REGION ICON
  // ═══════════════════════════════════════════════════════════════════════
  
  const getRegionIcon = (region: string) => {
    const icons: Record<string, string> = {
      americas: '🌎',
      europe: '🌍',
      asia: '🌏',
      oceania: '🌏',
      africa: '🌍'
    };
    return icons[region] || '🌐';
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleSearch = (query: string) => {
    setLocalQuery(query);
    search(query);
  };

  const handleSelect = (timezone: string) => {
    onChange(timezone);
    setIsOpen(false);
    setLocalQuery('');
    clearSearch();
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setLocalQuery('');
      clearSearch();
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════
  
  return (
    <div className={className}>
      {/* Label */}
      {label && (
        <Form.Label className="mb-2">
          <FontAwesomeIcon icon={faGlobe} className="me-2" style={{ color: '#2ca799' }} />
          {label}
        </Form.Label>
      )}

      {/* Selected Timezone Display */}
      <div
        onClick={handleToggle}
        style={{
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid var(--bs-border-color)',
          backgroundColor: 'var(--bs-body-bg)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          marginBottom: isOpen ? '8px' : '0'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#2ca799';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--bs-border-color)';
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <div style={{ fontWeight: '500', color: 'var(--bs-body-color)' }}>
              {getRegionIcon(selectedTimezone?.region || '')} {selectedTimezone?.label || value}
            </div>
            {timePreview && (
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginTop: '4px' }}>
                <FontAwesomeIcon icon={faClock} className="me-1" />
                Hora: {timePreview}
              </div>
            )}
          </div>
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              color: 'var(--bs-secondary-color)',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          style={{
            position: 'relative',
            borderRadius: '8px',
            border: '1px solid var(--bs-border-color)',
            backgroundColor: 'var(--bs-body-bg)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: '400px',
            overflow: 'hidden',
            zIndex: 1000
          }}
        >
          {/* Search Input */}
          <div style={{ padding: '12px', borderBottom: '1px solid var(--bs-border-color)' }}>
            <InputGroup size="sm">
              <InputGroup.Text style={{ backgroundColor: 'transparent', border: 'none' }}>
                <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--bs-secondary-color)' }} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar zona horaria..."
                value={localQuery}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  border: 'none',
                  backgroundColor: 'transparent',
                  boxShadow: 'none'
                }}
                autoFocus
              />
            </InputGroup>
          </div>

          {/* Detected Timezone */}
          {!searchQuery && detectedTimezone && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--bs-border-color)' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--bs-secondary-color)', marginBottom: '4px' }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                DETECTADA
              </div>
              <div
                onClick={() => handleSelect(detectedTimezone.value)}
                style={{
                  padding: '8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: value === detectedTimezone.value ? 'rgba(44, 167, 153, 0.1)' : 'transparent',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = value === detectedTimezone.value ? 'rgba(44, 167, 153, 0.15)' : 'var(--bs-secondary-bg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = value === detectedTimezone.value ? 'rgba(44, 167, 153, 0.1)' : 'transparent';
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <span style={{ marginRight: '8px' }}>{getRegionIcon(detectedTimezone.region)}</span>
                    <span style={{ fontWeight: '500' }}>{detectedTimezone.label}</span>
                  </div>
                  {value === detectedTimezone.value && (
                    <FontAwesomeIcon icon={faCheck} style={{ color: '#2ca799' }} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timezone List */}
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {displayTimezones.length > 0 ? (
              displayTimezones.map((tz) => (
                <div
                  key={tz.value}
                  onClick={() => handleSelect(tz.value)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    backgroundColor: value === tz.value ? 'rgba(44, 167, 153, 0.1)' : 'transparent',
                    borderBottom: '1px solid var(--bs-border-color)',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = value === tz.value ? 'rgba(44, 167, 153, 0.15)' : 'var(--bs-secondary-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = value === tz.value ? 'rgba(44, 167, 153, 0.1)' : 'transparent';
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <span style={{ marginRight: '8px' }}>{getRegionIcon(tz.region)}</span>
                      <span style={{ fontWeight: value === tz.value ? '600' : '400' }}>
                        {tz.label}
                      </span>
                      {previewDate && (
                        <span style={{ 
                          marginLeft: '12px', 
                          fontSize: '0.85rem', 
                          color: 'var(--bs-secondary-color)' 
                        }}>
                          {getTimeInTz(previewDate, tz.value)}
                        </span>
                      )}
                    </div>
                    {value === tz.value && (
                      <FontAwesomeIcon icon={faCheck} style={{ color: '#2ca799' }} />
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-muted mb-0">No se encontraron zonas horarias</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Info */}
      {showPreview && timePreview && !isOpen && (
        <div 
          style={{ 
            marginTop: '8px', 
            padding: '8px 12px', 
            borderRadius: '6px',
            backgroundColor: 'rgba(44, 167, 153, 0.05)',
            fontSize: '0.85rem',
            color: 'var(--bs-secondary-color)'
          }}
        >
          <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: '#2ca799' }} />
          Hora seleccionada: <strong>{timePreview}</strong> ({selectedTimezone?.abbr})
        </div>
      )}
    </div>
  );
};

export default TimeZoneSelector;